import { action, computed, observable } from 'mobx';

import { setSamples, removeOfferByCode } from '~api/SamplesAPI/SamplesAPI';
import { CartRepository } from '~repositories/CartRepository/CartRepository';
import OffersRepository from '~repositories/OffersRepository/OffersRepository';
import { SamplesRepository } from '~repositories/SamplesRepository/SamplesRepository';

interface SamplesControllerProps {
    samplesRepository: SamplesRepository;
    cartRepository: CartRepository;
    offersRepository: OffersRepository;
}

interface ISamplesList {
    [key: string]: string[];
}

export class SamplesController {
    @observable samplesRepository: SamplesRepository;
    @observable cartRepository: CartRepository;
    @observable offersRepository: OffersRepository;

    @observable private _samplesList: ISamplesList = {};
    @observable private _isLoading: boolean = false;

    constructor({ samplesRepository, cartRepository, offersRepository }: SamplesControllerProps) {
        this.samplesRepository = samplesRepository;
        this.cartRepository = cartRepository;
        this.offersRepository = offersRepository;
    }

    public getSelectedSamples = (offerCode: string) =>
        this.cartRepository.samplesInCart?.filter(
            ({ offerCode: incomingOfferCode }) => offerCode === incomingOfferCode
        ) || [];

    @action getSamples = () => this.samplesRepository.getSamples();
    @action getCart = () => this.cartRepository.getCart();

    @action public addSample = (offerId: string) => async (sku: string) => {
        this._isLoading = true;

        const currentSamples = await this.getSelectedSamples(offerId).map(({ skuId }) =>
            skuId?.toString()
        );

        this._samplesList[offerId] = [...currentSamples, sku];

        await setSamples(this._samplesList);
        await this.cartRepository.getCart();
        await this.samplesRepository.getSamples();

        const samplesProductSku = await this.getSelectedSamples(offerId).map(({ skuId }) =>
        skuId?.toString().trim()
        );

        this._isLoading = false;

        const samplesProductName = await this.getSelectedSamples(offerId).map(({ product }) =>
        product.name?.toString().trim()
        );
        const currentSamplesProduct = {
            prodName: samplesProductName,
            prodSku: samplesProductSku
        };
        if (samplesProductName && samplesProductSku) {
            window.dispatchEvent(new CustomEvent('cart.sampleAdded', { detail: currentSamplesProduct }));
        }
    };

    @action public removeSample = (offerId: string) => async (sku: string) => {
        this._isLoading = true;

        const currentSamples = await this.getSelectedSamples(offerId).map(({ skuId }) =>
            skuId.toString().trim()
        );
        const currentRemovedSamples = await this.getSelectedSamples(offerId).map(({ product }) =>
        product.name.toString().trim()
        );

        const indexToRemove = currentSamples.indexOf(sku);
        const removedProductName = currentRemovedSamples[indexToRemove];
        currentSamples.splice(indexToRemove, 1);
        this._samplesList[offerId] = currentSamples;

        if (currentSamples?.length === 0) {
            await removeOfferByCode(offerId);
        } else {
            await setSamples(this._samplesList);
        }
        await this.cartRepository.getCart();
        await this.samplesRepository.getSamples();

        this._isLoading = false;
        const currentRemovedproduct = {
            prodName: removedProductName,
            prodSku: sku
        };

        if (currentRemovedSamples && currentSamples) {
            window.dispatchEvent(new CustomEvent('cart.sampleRemoved', { detail: currentRemovedproduct }));
        }
    };

    @computed public get samples() {
        return this.samplesRepository.samples;
    }

    @computed public get loyalty() {
        return this.samplesRepository.loyalty;
    }

    @computed public get cartSamples() {
        return this.cartRepository.samplesInCart;
    }

    @computed public get isLoading() {
        return this.cartRepository.isLoading || this.samplesRepository.isLoading || this._isLoading;
    }
}
